/**
 * PrivacyIt - l'informativa sulla privacy, in italiano
 * -----------------------------------------------------
 * The Italian half of the privacy notice. `PrivacyEn.jsx` is the English one
 * and `PrivacyPolicy.jsx` picks between them.
 *
 * THE TWO FILES MUST SAY THE SAME THING. Change one and change the other in
 * the same commit, and bump TERMS_VERSION. `npm run check-legal` compares
 * their shape.
 *
 * Written with "tu", like the terms and unlike the consent emails - the same
 * reasoning: a sixteen-year-old has to be able to read it, and the Garante
 * expects that of a service aimed at minors.
 *
 * NOTE ON LEGAL TERMS. Where a phrase is a term of art in the GDPR, the
 * Italian uses the wording the Regulation itself uses in Italian -
 * "titolare del trattamento" for data controller, "responsabile della
 * protezione dei dati" for DPO, "interesse legittimo", "portabilita". A
 * literal translation of the English would read as an approximation to
 * anybody who has seen the Regulation, and this is the document the Garante
 * would read first.
 */

import { Link } from 'react-router-dom';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'ChoreConnect';
const CONTACT_EMAIL = 'hello@choreconnect.org';
const SAFETY_EMAIL = 'safety@choreconnect.org';

export default function PrivacyIt() {
  return (
    <>
      <div className="card">
        <h2 style={{ fontSize: 'var(--text-h3)' }}>In breve</h2>
        <p style={{ marginTop: 'var(--space-xs)' }}>
          Conserviamo i dati che ci fornisci perché chi ha bisogno di un lavoretto possa trovare
          uno studente che lo faccia.{' '}
          <strong>Non vendiamo mai i tuoi dati e non li usiamo mai per fare pubblicità.</strong>{' '}
          Puoi chiederci in qualsiasi momento di mostrarti cosa conserviamo, di correggerlo o di
          cancellarlo. Se hai meno di 18 anni, serve il consenso di un genitore o tutore prima che
          tu possa lavorare.
        </p>
        <p style={{ marginTop: 'var(--space-xs)' }}>
          Due cose da sapere subito:{' '}
          <strong>i messaggi su questo sito vengono conservati e possono essere letti da un
          moderatore</strong>{' '}
          se una conversazione viene segnalata (sezione 6), e{' '}
          <strong>se pubblichi quando sei disponibile, gli adulti iscritti al sito possono
          vederlo</strong>{' '}
          (sezione 5).
        </p>
        <p className="text-sm text-muted" style={{ marginTop: 'var(--space-xs)' }}>
          Il resto di questa pagina è il dettaglio che la legge ci impone di darti.
        </p>
      </div>

      <hr className="divider" />

      {/* ============================================================= */}
      <h2>1. Chi è responsabile</h2>
      <p>
        Il &ldquo;titolare del trattamento&rdquo; - la persona giuridicamente responsabile dei tuoi
        dati - è <strong>Evelyn Catelijne Brouwer</strong>.
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </li>
        <li>
          <strong>Sicurezza e segnalazioni:</strong>{' '}
          <a href={`mailto:${SAFETY_EMAIL}`}>{SAFETY_EMAIL}</a>
        </li>
        <li>
          {/* Inserito il 28 agosto 2026, su indicazione del titolare. L'art.
              13(1)(a) del GDPR rende obbligatorio un indirizzo postale, ed era
              l'ultimo segnaposto rimasto nei due documenti.

              Identico alla versione inglese: se uno dei due viene corretto,
              vanno corretti entrambi. */}
          <strong>Indirizzo postale:</strong>{' '}
          Via Guglielmo Marconi 249, 21027 Ispra (VA), Italia
        </li>
      </ul>
      <p>
        Non abbiamo nominato un responsabile della protezione dei dati. Riteniamo di non esservi
        tenuti; se questo dovesse cambiare, lo indicheremo qui insieme ai suoi recapiti.
      </p>

      {/* ============================================================= */}
      <h2>2. Cosa raccogliamo</h2>

      <h3>Tutti</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Nome e indirizzo email</strong> - per creare il tuo account e farti accedere.
        </li>
        <li>
          <strong>Password</strong> - conservata cifrata, così nessuno a {APP_NAME} può leggerla.
        </li>
        <li>
          <strong>Il tuo comune e la tua via e, se lo scegli, la tua posizione esatta</strong> -
          per mostrare quanto è distante un lavoretto, su strada e a piedi. La posizione esatta è
          facoltativa e puoi rifiutarla; premendo &ldquo;Usa la mia posizione esatta&rdquo; compiliamo
          via e comune al posto tuo, e puoi correggerli a mano. La tua via è conservata sul tuo
          account e non viene mostrata a nessun altro: agli altri diciamo il comune e una distanza.
          Le distanze sono calcolate tra la via del tuo account e la via del lavoretto, e solo
          quando riusciamo a trovarle entrambe sulla mappa; quando non ci riusciamo, non mostriamo
          alcuna distanza a nessuno. La posizione del tuo browser serve solo a suggerirti quella
          via, mai a calcolare distanze. Vedi la sezione 7 per la provenienza dei nomi delle vie.
        </li>
        <li>
          <strong>Numero di telefono</strong> - facoltativo.
        </li>
        <li>
          <strong>Foto del profilo</strong> - facoltativa, sia per gli studenti sia per i
          committenti.
        </li>
        <li>
          <strong>Se hai confermato il tuo indirizzo email</strong>, e il codice monouso che ti
          inviamo per farlo.
        </li>
      </ul>

      <h3>Studenti</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>La scuola che frequenti</strong>, così come la scrivi - mostrata sul tuo profilo.
          Non la verifichiamo: a decidere chi può iscriversi è il codice di invito.
        </li>
        <li>
          <strong>Data di nascita e anno scolastico</strong> - per verificare che tu sia abbastanza
          grande per lavorare, per tenere aggiornata la tua età man mano che cresci, e per mostrare
          ai committenti chi stanno ingaggiando. Gli altri vedono la tua età, mai la tua data di
          nascita.
        </li>
        <li>
          <strong>Quello che scrivi su di te</strong> - facoltativo, e pubblico.
        </li>
        <li>
          <strong>Coordinate bancarie</strong> - facoltative. Visibili solo a te; non mostrate mai
          a nessun altro utente e mai inviate altrove.
        </li>
        <li>
          <strong>Nome ed email del tuo genitore o tutore</strong> - se hai meno di 18 anni,
          conservati come registro del fatto che sono stati interpellati e di cosa hanno risposto.
        </li>
        <li>
          <strong>La tua disponibilità</strong>, se la pubblichi - in cosa puoi dare una mano, in
          quale comune, e in quali momenti della settimana sei di solito libero.
        </li>
      </ul>

      <h3>Tutto quello che fai qui</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Lavoretti, candidature, disponibilità, recensioni e messaggi</strong> che crei.
          Un lavoretto comprende sempre l&rsquo;indirizzo dove si svolge.
        </li>
        <li>
          <strong>Una donazione, se scegli di farla</strong> - l&rsquo;importo, la data e il
          riferimento di Stripe al pagamento, collegati al tuo account. I dati della tua carta vanno
          a Stripe e non arrivano mai a noi.
        </li>
        <li>
          <strong>Le segnalazioni</strong> che fai su qualcuno, e chi hai bloccato.
        </li>
        <li>
          <strong>Le impostazioni delle notifiche</strong> e - se attivi gli avvisi - un{' '}
          <strong>abbonamento per il tuo browser o telefono</strong> per potertele inviare.
          Identifica il dispositivo, non te personalmente.
        </li>
      </ul>

      <h3>Registri su come viene gestito il sito</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Il tuo indirizzo IP e l&rsquo;orario</strong> quando ti registri, fai una
          segnalazione, o quando un moderatore interviene. Li usiamo per mantenere sicuro il sito e
          per approfondire gli abusi.
        </li>
        <li>
          <strong>Un registro delle attività</strong> che annota azioni come la creazione, la
          sospensione o il ripristino di account, il consenso dato o revocato, i lavoretti
          pubblicati o rimossi, e{' '}
          <strong>ogni volta che un moderatore apre una conversazione</strong>. Vedi la sezione 6.
        </li>
        <li>
          <strong>Registri di moderazione</strong> - se un account viene sospeso conserviamo il
          motivo, quando è iniziata, quando finisce e chi ha deciso. Se un lavoretto o una
          disponibilità vengono rimossi, conserviamo il motivo.
        </li>
        <li>
          <strong>Tutto quello che scrivi in &ldquo;Segnala un problema&rdquo;</strong>, e le nostre
          risposte. Vedi la sezione 6.
        </li>
        <li>
          <strong>Solo per i moderatori</strong> - i dati di sicurezza aggiuntivi per il loro
          accesso in due passaggi.
        </li>
      </ul>

      {/* ============================================================= */}
      <h2>3. Perché possiamo conservarli</h2>
      <p>La legge ci impone di indicare una base giuridica per ogni finalità. Le nostre sono:</p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Per fornire il servizio che ci hai chiesto</strong> - il tuo account, i
          lavoretti, le candidature, le disponibilità, le recensioni e i messaggi.{' '}
          <em>Base: esecuzione di un contratto (art. 6(1)(b)).</em> Senza questi non potremmo far
          funzionare {APP_NAME}.
        </li>
        <li>
          <strong>Per tutelare le persone e fermare gli abusi</strong> - segnalazioni, blocchi,
          registri di moderazione, il registro delle attività, gli indirizzi IP, e la lettura da
          parte di un moderatore di una conversazione segnalata.{' '}
          <em>Base: nostro legittimo interesse (art. 6(1)(f)).</em> L&rsquo;interesse è proteggere
          gli utenti, in gran parte minorenni, da danni su un servizio che li mette in contatto con
          adulti. Lo abbiamo bilanciato con la tua riservatezza e conserviamo solo quanto serve a
          quella finalità.
        </li>
        <li>
          <strong>Per conservare la prova che un genitore ha acconsentito</strong> - il registro
          del consenso stesso.{' '}
          <em>Base: obbligo legale e legittimo interesse (art. 6(1)(c) e (f)).</em> Esiste per
          tutelare il minore interessato e per dimostrare che abbiamo chiesto.
        </li>
        <li>
          <strong>Per registrare una donazione che hai scelto di fare</strong> - così che il denaro
          possa essere confrontato con i registri di Stripe e contabilizzato.{' '}
          <em>Base: esecuzione di un contratto (art. 6(1)(b)) e obbligo legale di conservare le
          registrazioni del denaro ricevuto (art. 6(1)(c)).</em>
        </li>
        <li>
          <strong>Per tenere sicuro il tuo account</strong> - conferma dell&rsquo;indirizzo email,
          reimpostazione della password, accesso in due passaggi per i moderatori.{' '}
          <em>Base: esecuzione di un contratto, e legittimo interesse alla sicurezza.</em>
        </li>
        <li>
          <strong>Le cose facoltative che attivi tu</strong> - la posizione esatta, la fotografia,
          quello che scrivi su di te e gli avvisi sul telefono.{' '}
          <em>Base: il tuo consenso (art. 6(1)(a)).</em> Puoi revocarlo in qualsiasi momento
          rimuovendole o disattivandole, e questo non pregiudica quanto fatto in precedenza.
        </li>
      </ul>
      <p>
        <strong>Non inviamo comunicazioni commerciali e non abbiamo pubblicità.</strong> Tutto
        quello che ti scriviamo è qualcosa che hai chiesto o qualcosa che devi ricevere.
      </p>

      {/* ============================================================= */}
      <h2>4. Chi può vedere cosa</h2>
      <p>
        Questa è la sezione più importante della pagina, quindi è breve e precisa.
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Chiunque, anche chi non ha effettuato l&rsquo;accesso</strong> - i lavoretti in
          bacheca e il nome di battesimo di chi li ha pubblicati. La bacheca non contiene alcun
          dato degli studenti.
        </li>
        <li>
          <strong>Gli utenti che hanno effettuato l&rsquo;accesso</strong> - il tuo nome e
          l&rsquo;iniziale del cognome, il tuo comune, la tua valutazione e le tue recensioni, e la
          tua foto se ne hai aggiunta una. Per gli studenti, anche l&rsquo;età, la scuola,
          l&rsquo;anno scolastico, quello che hai scritto su di te e le categorie che hai scelto.
        </li>
        <li>
          <strong>I committenti adulti, se sei uno studente che pubblica la propria
          disponibilità</strong> - in cosa puoi dare una mano, il tuo comune e gli orari in cui sei
          di solito libero. Vedi la sezione 5.
        </li>
        <li>
          <strong>Solo le persone coinvolte in un lavoretto</strong> - il tuo nome completo e
          l&rsquo;indirizzo esatto. Il committente vede lo studente che ha accettato e la riserva;
          quegli studenti vedono l&rsquo;indirizzo. Nessun altro.
        </li>
        <li>
          <strong>Solo tu</strong> - la tua password, le tue coordinate bancarie, il tuo numero di
          telefono, le tue coordinate esatte, la tua data di nascita, i recapiti del tuo genitore o
          tutore e le eventuali donazioni che hai fatto.
        </li>
        <li>
          <strong>I moderatori</strong> - vedi la sezione 6.
        </li>
      </ul>

      {/* ============================================================= */}
      <h2>5. Se pubblichi quando sei disponibile</h2>
      <p>
        Gli studenti possono pubblicare la propria <strong>disponibilità</strong>: in cosa possono
        dare una mano, in quale comune, e un orario settimanale di quando sono di solito liberi. È
        facoltativo e puoi metterlo in pausa o eliminarlo in qualsiasi momento.
      </p>
      <p>
        <strong>È importante capire cosa comporta.</strong> Ovunque altro su {APP_NAME} sei tu a
        cercare lavoro. Qui funziona al contrario: sono gli adulti iscritti al sito a guardare i
        profili degli studenti e a chiedere. Quindi:
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Chi non ha effettuato l&rsquo;accesso non vede niente</strong>, e nemmeno gli
          altri studenti. Solo i committenti adulti che hanno effettuato l&rsquo;accesso.
        </li>
        <li>
          <strong>Se hai meno di 18 anni, il tuo genitore ha già acconsentito a questo in modo
          specifico</strong>, non ai lavoretti in generale.
        </li>
        <li>
          <strong>Nessuno può prenotarti.</strong> Un committente può soltanto chiedere. Tu vedi il
          lavoro, il compenso e il comune, e decidi se accettare. Non devi mai dare una
          motivazione per rifiutare.
        </li>
        <li>
          <strong>Nessun indirizzo viene condiviso finché non accetti.</strong>
        </li>
      </ul>
      <p>
        Pensa a cosa scrivi. &ldquo;Libero il martedì dopo scuola&rdquo; va bene. Qualsiasi cosa
        indichi quale autobus prendi, da quale cancello esci o dove ti trovi ad aspettare, no.
      </p>

      {/* ============================================================= */}
      <h2>6. I messaggi, e quando un moderatore può leggerli</h2>
      <p>
        Uno studente può fare una domanda a chi ha pubblicato un lavoretto ancora aperto alle
        candidature e, una volta accettato il lavoretto, i due possono scriversi al riguardo. Solo
        lo studente può iniziare queste conversazioni - chi pubblica non può mai scrivere per primo
        a uno studente - e una domanda diventa di sola lettura non appena il lavoretto viene
        assegnato.
      </p>
      <p>
        <strong>C&rsquo;è una sola conversazione che può iniziare un adulto, ed è volutamente
        stretta.</strong> Un adulto può rivolgere una singola domanda a uno studente sulla
        disponibilità che ha pubblicato. Lo studente riceve una notifica, e non si può inviare
        altro se non risponde: la risposta è ciò che apre la conversazione, e ignorarla chiude la
        questione. Un adulto può avere al massimo cinque domande senza risposta per volta. Dobbiamo
        essere chiari su quanto sia privato tutto questo.
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>I messaggi vengono conservati e non sono cifrati end-to-end.</strong> È il
          contrario di quello che si costruirebbe per due adulti, ed è voluto: un moderatore deve
          poter leggere una conversazione oggetto di una segnalazione.
        </li>
        <li>
          <strong>I messaggi non vengono mai cancellati</strong>, nemmeno alla chiusura di un
          account. Un moderatore può nascondere un messaggio alle due persone della conversazione;
          il testo resta nei nostri registri perché può costituire una prova.
        </li>
        <li>
          <strong>Un moderatore può aprire una conversazione in due soli casi:</strong> qualcuno
          l&rsquo;ha segnalata - la persona, il lavoretto, o un messaggio al suo interno - oppure
          il nostro sistema ha rilevato uno scambio di contatti, che è lo schema tipico di una
          conversazione spostata dove nessuno può vederla.{' '}
          <strong>Ogni altra conversazione gli è preclusa</strong>, e il server la rifiuta anziché
          limitarsi a nascondere il collegamento.
        </li>
        <li>
          <strong>Ogni volta che un moderatore apre una conversazione viene annotato nel registro
          delle attività</strong> - chi ha letto, di chi era la conversazione, quando, e quanti
          messaggi sono stati mostrati.
        </li>
        <li>
          <strong>Una conversazione si chiude sette giorni dopo il lavoretto.</strong> Non è un
          canale a tempo indeterminato.
        </li>
      </ul>
      <p>
        Nessuno legge le conversazioni di routine, e nulla viene esaminato da una persona a meno
        che non ricorra uno dei due casi sopra.
      </p>

      <h3>Se segnali un problema</h3>
      <p>
        Oltre a quanto sopra, ogni iscritto ha una conversazione &ldquo;Segnala un problema&rdquo;
        per dirci che il sito stesso non funziona. Funziona in modo diverso dalle conversazioni
        descritte sopra, e le differenze contano.
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Un moderatore la legge tutta, sempre.</strong> La regola delle due situazioni qui
          sopra non si applica, perch&eacute; stai scrivendo a lui e non a un altro iscritto. Per lo
          stesso motivo la lettura non finisce nel registro delle attivit&agrave;; la risposta
          s&igrave;.
        </li>
        <li>
          <strong>Non ti viene detto quale moderatore ha risposto.</strong> Le risposte compaiono
          come &ldquo;Moderatore&rdquo;, senza nome, foto o profilo, e il sito non invia mai quelle
          informazioni al tuo browser. Un moderatore qui &egrave; anche un iscritto come gli altri, e
          chi ha segnalato un problema non deve passare la settimana successiva a riconoscerlo.
        </li>
        <li>
          <strong>Viene conservata, anche dopo la chiusura del tuo account</strong>, e non viene mai
          eliminata. Le persone non suddividono i propri problemi nelle nostre categorie prima di
          scrivere, quindi una conversazione su qualcosa che non funziona &egrave; a volte l&rsquo;unica
          traccia scritta del fatto che qualcuno si &egrave; sentito a disagio con una persona.
        </li>
      </ul>

      {/* ============================================================= */}
      <h2>7. Chi altro tratta i tuoi dati</h2>
      <p>
        Non vendiamo i tuoi dati e non li usiamo per fare pubblicità. Li condividiamo solo con i
        servizi che fanno funzionare il sito:
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>MongoDB Atlas</strong> (regione UE) - conserva il database.
        </li>
        <li>
          <strong>Hetzner</strong> (Germania) - gestisce il server che eroga il sito.
        </li>
        <li>
          <strong>Cloudflare</strong> - si trova davanti al sito, cifra la connessione e filtra gli
          attacchi, quindi ogni richiesta vi passa attraverso.
        </li>
        <li>
          <strong>Brevo</strong> (Francia) - invia le nostre email, come la reimpostazione della
          password, le richieste di consenso ai genitori e le conferme di indirizzo.
        </li>
        <li>
          <strong>Cloudinary</strong> - conserva le foto del profilo.
        </li>
        <li>
          <strong>Il servizio di notifiche del tuo browser o del produttore del telefono</strong>{' '}
          (Google, Apple, Mozilla o Microsoft, a seconda del dispositivo) - recapita gli avvisi, se
          li attivi. Vedono che un messaggio è stato inviato al tuo dispositivo.
        </li>
        <li>
          <strong>Stripe</strong> - solo se scegli di fare una donazione, e solo in quel caso.
          Stripe riceve il tuo indirizzo email e l&rsquo;importo, raccoglie direttamente i dati
          della tua carta e ti invia la ricevuta via email. Non vediamo mai i dati della tua carta.
          Usare {APP_NAME} non ha costi.
        </li>
      </ul>
      <p>
        <strong>I nomi delle vie, i percorsi e la tua posizione.</strong> Quando premi &ldquo;Usa la
        mia posizione esatta&rdquo;, la via viene cercata sul nostro server, in una copia dei dati
        cartografici di OpenStreetMap (&copy; contributori di OpenStreetMap). Anche le distanze su
        strada vengono calcolate sul nostro server, da un programma di calcolo dei percorsi che usa
        gli stessi dati. La tua posizione, la via che scrivi e i luoghi tra cui misuriamo le distanze
        non vengono inviati a OpenStreetMap né a nessun altro servizio di mappe o di percorsi. Dal
        18 settembre 2026 la posizione del tuo browser non ci viene inviata affatto mentre navighi:
        la bacheca è misurata a partire dalla via del tuo account.
      </p>
      <p>
        Trasmetteremmo inoltre i dati alle forze dell&rsquo;ordine, a un giudice o ad
        un&rsquo;altra autorità qualora la legge lo imponesse, o qualora qualcuno fosse a rischio
        di un danno grave.
      </p>
      <p>
        <strong>Dove si trovano i tuoi dati.</strong> Il tuo account e tutto ciò che contiene sono
        conservati nell&rsquo;Unione Europea - il database in una regione UE, il server in
        Germania. <strong>Cloudflare, Cloudinary e Stripe sono società statunitensi, o ne fanno
        parte,</strong> e possono
        trattare dati negli Stati Uniti; in tal caso il trasferimento è coperto da una garanzia
        adeguata ai sensi del Capo V del GDPR, che per questi fornitori è la certificazione nel
        quadro dell&rsquo;EU-US Data Privacy Framework oppure le clausole contrattuali tipo della
        Commissione europea. Abbiamo un accordo sul trattamento dei dati con ciascun fornitore.
      </p>

      {/* ============================================================= */}
      <h2>8. Cookie e cosa salviamo nel tuo browser</h2>
      <p>
        <strong>Non usiamo alcun cookie di analisi, tracciamento o pubblicità. Su questo sito non
        c&rsquo;è alcun tracciamento di terze parti.</strong>
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Il tuo token di accesso</strong>, conservato nella memoria locale per mantenerti
          connesso. Dura fino a un anno, così un sito che hai aggiunto alla schermata home non ti
          disconnette tra una visita e l&rsquo;altra, e sparisce nel momento in cui esci. È
          strettamente necessario al funzionamento del sito e non è condiviso con nessuno.
        </li>
        <li>
          <strong>Un modulo di iscrizione non completato</strong>, conservato solo finché quella
          scheda del browser resta aperta, così leggere questa informativa o i termini a metà
          iscrizione non ti fa perdere quello che avevi scritto. Viene eliminato quando l&rsquo;account
          viene creato, quando chiudi la scheda, o dopo due ore - quale che sia la prima. Non lascia
          mai il tuo dispositivo.
        </li>
        <li>
          <strong>Cloudflare può impostare un cookie di sicurezza</strong> per distinguere i
          visitatori reali dagli attacchi automatizzati. È strettamente necessario alla sicurezza e
          non viene usato per tracciarti su altri siti.
        </li>
      </ul>
      <p>
        Poiché tutti e tre sono strettamente necessari, per essi non è richiesto alcun banner di
        consenso. Se dovessimo mai aggiungere qualcosa che non lo è, te lo chiederemo prima.
      </p>

      {/* ============================================================= */}
      <h2>9. Per quanto tempo li conserviamo</h2>
      <p>
        <strong>Preferiamo dirti cosa succede davvero piuttosto che citare un numero
        rassicurante.</strong>
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Il tuo account</strong> - finché non lo chiudi. Se smetti di usare {APP_NAME}{' '}
          puoi chiudere l&rsquo;account in qualsiasi momento dalle impostazioni, e ti
          incoraggiamo a farlo.
        </li>
        <li>
          <strong>Quando chiudi il tuo account</strong> - nome, email, numero di telefono,
          fotografia, posizione, data di nascita, coordinate bancarie e tutto quello che avevi
          scritto su di te vengono rimossi immediatamente. La tua foto viene cancellata da Cloudinary nello stesso
          momento.
        </li>
        <li>
          <strong>Lavoretti, recensioni, messaggi e quanto hai scritto segnalando un
          problema</strong> - restano dopo la chiusura dell&rsquo;account, con il tuo nome sostituito
          da &ldquo;Utente eliminato&rdquo;. Li conserviamo perché cancellarli porterebbe via con
          sé lo storico e le recensioni di altre persone, e perché i messaggi potrebbero servire
          per approfondire qualcosa che è successo.
        </li>
        <li>
          <strong>I registri del consenso dei genitori</strong> - conservati finché esiste
          l&rsquo;account e dopo la sua chiusura, come prova che abbiamo chiesto e di cosa è stato
          risposto.
        </li>
        <li>
          <strong>Le registrazioni delle donazioni</strong> - conservate dopo la chiusura
          dell&rsquo;account, perché le registrazioni del denaro ricevuto vanno tenute a fini
          contabili e fiscali.
        </li>
        <li>
          <strong>Il registro delle attività, i registri di moderazione, le segnalazioni e gli
          indirizzi IP a essi collegati</strong> - conservati come prova di cosa è stato fatto e
          perché.
        </li>
      </ul>
      <p>
        <strong>Dal 25 settembre 2026 questi dati vengono cancellati allo scadere di un
        termine.</strong> Prima di quella data non veniva cancellato nulla, e questa informativa lo
        diceva. I termini sono:
      </p>
      <ul>
        <li>
          <strong>Una voce del registro delle attività - due anni</strong>, poi viene cancellata.
        </li>
        <li>
          <strong>Un indirizzo IP - un anno</strong>, sia che si trovi su una voce del registro sia
          su una segnalazione. La registrazione resta; l&rsquo;indirizzo viene rimosso. È la parte
          che indica una casa e non un account, quindi è quella che conserviamo per meno tempo.
        </li>
        <li>
          <strong>Una segnalazione che un moderatore ha chiuso - tre anni</strong> dal giorno in cui
          è stata chiusa, poi viene cancellata. <strong>Una segnalazione di cui nessuno si è ancora
          occupato non viene mai cancellata allo scadere di un termine</strong>: resta finché una
          persona non la chiude.
        </li>
      </ul>
      <p>
        Puoi comunque chiederci di cancellare i tuoi dati personali in qualsiasi momento
        (sezione 10) - lo faremo a meno che non abbiamo un motivo legale per conservare qualcosa, e
        in tal caso te lo diremo.
      </p>

      {/* ============================================================= */}
      <h2>10. I tuoi diritti</h2>
      <p>Ai sensi del GDPR puoi chiederci di:</p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>mostrarti tutto quello che conserviamo su di te (accesso)</li>
        <li>correggere quello che è sbagliato (rettifica)</li>
        <li>cancellare i tuoi dati personali (cancellazione)</li>
        <li>
          sospendere l&rsquo;uso dei tuoi dati mentre esaminiamo un reclamo (limitazione)
        </li>
        <li>darti una copia in un formato che puoi portare altrove (portabilità)</li>
        <li>
          interrompere i trattamenti fondati sul nostro legittimo interesse (opposizione) - dicci
          perché ti riguarda e ci fermeremo, a meno che non sussistano motivi cogenti per
          proseguire
        </li>
        <li>revocare in qualsiasi momento un consenso dato per le parti facoltative</li>
      </ul>
      <p>
        Scrivi a <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Risponderemo entro un
        mese, e non costa nulla. Potremmo prima chiederti di confermare la tua identità, così che
        nessun altro possa chiedere i tuoi dati.
      </p>
      <p>
        <strong>Se hai meno di 18 anni</strong>, il tuo genitore o tutore può fare tutto questo per
        te. Può anche revocare il proprio consenso in qualsiasi momento, usando il link
        nell&rsquo;email che ha ricevuto - non scade. Revocare il consenso ti impedisce di
        candidarti, di pubblicare la tua disponibilità o di essere prenotato; non cancella il tuo
        account, e viene comunicato a entrambi.
      </p>

      {/* ============================================================= */}
      <h2>11. Minori</h2>
      <p>
        Devi avere almeno 16 anni per registrarti come studente, perché 16 è l&rsquo;età minima per
        lavorare in Italia. Chiediamo la tua data di nascita e prendiamo per buona la tua risposta:
        non la verifichiamo. Dopo la registrazione non si può cambiare dal tuo account, così che
        nessuno possa usarla per aggirare le regole sull&rsquo;età; se è sbagliata, avvisaci.
      </p>
      <p>
        <strong>Se hai meno di 18 anni scriviamo al tuo genitore o tutore e aspettiamo.</strong>{' '}
        Finché non confermano non puoi candidarti, pubblicare quando sei disponibile, né essere
        prenotato da nessuno. Non accettiamo deliberatamente una casella spuntata da te al loro
        posto: una casella che spunti tu non dimostra che un genitore abbia mai visto la richiesta,
        tanto meno che vi abbia acconsentito. Dal tuo diciottesimo compleanno la loro conferma non
        serve più.
      </p>
      <p>Diciamo loro a cosa stanno acconsentendo, in cinque punti:</p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>che puoi candidarti ai lavoretti e recarti all&rsquo;indirizzo di qualcuno</li>
        <li>
          che un lavoretto può durare più giorni, quindi puoi accettare una volta sola di andare
          più volte a casa della stessa persona adulta
        </li>
        <li>
          che puoi pubblicare quando sei disponibile, perché gli adulti iscritti al sito lo vedano
        </li>
        <li>
          che puoi fare una domanda su un lavoretto prima di candidarti, che tu e la persona di cui
          accetti il lavoretto potete scrivervi qui, e che quei messaggi vengono conservati e
          possono essere letti da un moderatore se segnalati
        </li>
        <li>che puoi scegliere di ricevere avvisi sul telefono</li>
      </ul>
      <p>
        Se dovessimo mai cambiare cosa ti succede qui, glielo chiediamo di nuovo anziché basarci
        sulla risposta già data.
      </p>
      <p>
        In Italia chiunque abbia almeno 14 anni può acconsentire al trattamento dei propri dati.
        Chiediamo comunque a un genitore, perché qui si tratta di più che di dati: comporta
        accettare i nostri termini e recarsi a casa di qualcun altro.
      </p>

      {/* ============================================================= */}
      <h2>12. Come teniamo al sicuro i tuoi dati</h2>
      <p>
        Le password sono conservate cifrate, così nessuno a {APP_NAME} può leggerle. La connessione
        è cifrata, l&rsquo;accesso al database è limitato, e gli account che possono leggere
        segnalazioni e conversazioni private richiedono un secondo passaggio oltre alla password
        per accedere.
      </p>
      <p>
        <strong>Nessun sistema è mai completamente sicuro.</strong> Se si verificasse una
        violazione tale da mettere le persone a rischio, lo comunicheremmo al Garante entro 72 ore
        da quando ne siamo venuti a conoscenza, come impone la legge, e lo comunicheremmo a te
        senza ingiustificato ritardo laddove il rischio per te fosse elevato.
      </p>

      {/* ============================================================= */}
      <h2>13. Decisioni automatizzate</h2>
      <p>
        <strong>Non prendiamo decisioni automatizzate che producano effetti giuridici o
        analogamente significativi nei tuoi confronti.</strong> A decidere chi ottiene un lavoretto
        è sempre una persona.
      </p>
      <p>
        Alcune cose sul sito sono automatiche, ed è giusto che tu sappia quali: la bacheca è
        ordinata per distanza, compenso, data o pertinenza rispetto a quello che cerchi, con i
        lavoretti che cercano solo una riserva in fondo; gli avvisi sui lavoretti sono abbinati alle
        categorie, al compenso minimo e alla distanza scelti dallo studente, e trattenuti durante
        le sue ore di silenzio; e un semplice controllo automatico segnala un messaggio che sembra
        contenere uno scambio di contatti, il che rende quella conversazione leggibile da un
        moderatore (sezione 6). Nessuna di queste decide alcunché su di te da sola.
      </p>

      {/* ============================================================= */}
      <h2>14. Se non sei soddisfatto</h2>
      <p>
        Diccelo prima di tutto: preferiamo rimediare. Scrivi a{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
      <p>
        Hai anche il diritto di proporre reclamo all&rsquo;autorità di controllo italiana, il{' '}
        <strong>Garante per la protezione dei dati personali</strong> (
        <a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">
          garanteprivacy.it
        </a>
        ), o all&rsquo;autorità del Paese in cui vivi.
      </p>

      {/* ============================================================= */}
      <h2>15. Modifiche</h2>
      <p>
        Se cambiamo qualcosa di importante aggiorneremo la data in cima alla pagina e te lo
        diremo. Se una modifica riguarda in modo sostanziale gli studenti minorenni, chiediamo di
        nuovo al loro genitore o tutore anziché basarci sulla risposta già data.
      </p>

      <hr className="divider" />

      <p className="text-sm text-muted">
        Vedi anche i nostri <Link to="/terms">termini di servizio</Link>.
      </p>
    </>
  );
}
